
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Container, Typography, Card, Paper, Grid, Tabs, Tab,
  IconButton, Button, CircularProgress, Divider, Alert, Chip, Skeleton
} from '@mui/material';
import {
  ContentCopy as CopyIcon, Share as ShareIcon, CardGiftcard as GiftIcon,
  AccountBalanceWallet as WalletIcon, ReceiptLong as ActivityIcon, ArrowBack as ArrowBackIcon,
  Schedule as ScheduleIcon, Lock as LockIcon, WorkspacePremium as WorkspacePremiumIcon,
  Science as ScienceIcon, Medication as MedicationIcon, LocalHospital as LocalHospitalIcon,
  SelfImprovement as SelfImprovementIcon, TrendingUp as TrendingUpIcon,
  ArrowForward as ArrowForwardIcon, Bolt as BoltIcon
} from '@mui/icons-material';

import { useNavigate } from "react-router-dom";

import { apiRequest } from '../../../fetchSetup';
import CustomDialog from '../../../CustomDialog';

const REFERRAL_API = process.env.REACT_APP_REFERRAL_API;

// ─── Adjust these routes to match your actual booking pages ────────────────
const SERVICE_META = {
  diagnostic: {
    label: 'Diagnostic Tests',
    tagline: 'Book a lab test and redeem points against the bill',
    icon: ScienceIcon,
    route: '/medicine-diagnostic',
    accent: '#0369A1',
    gradient: 'linear-gradient(135deg, #0EA5E9 0%, #075985 100%)',
  },
  medicine: {
    label: 'Medicine Orders',
    tagline: 'Order medicines and offset part of the cost with points',
    icon: MedicationIcon,
    route: '/medicine-order-type',
    accent: '#C2410C',
    gradient: 'linear-gradient(135deg, #FB923C 0%, #C2410C 100%)',
  },
  nursing: {
    label: 'Nursing Care',
    tagline: 'Book home nursing care and use your points toward it',
    icon: LocalHospitalIcon,
    route: '/NursingPhysioBooking',
    accent: '#047857',
    gradient: 'linear-gradient(135deg, #34D399 0%, #047857 100%)',
  },
  physiotherapy: {
    label: 'Physiotherapy',
    tagline: 'Book a session and put your reward points to use',
    icon: SelfImprovementIcon,
    route: '/NursingPhysioBooking',
    accent: '#6D28D9',
    gradient: 'linear-gradient(135deg, #A78BFA 0%, #5B21B6 100%)',
  },
};

const formatDate = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) +
    ', ' + d.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const formatDateOnly = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const daysUntil = (iso) => {
  if (!iso) return null;
  const diffMs = new Date(iso).getTime() - Date.now();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

// ─── Reward coin — a premium "medal" badge instead of a plain currency coin ─
// Layered gold gradient + embossed ring + a slow diagonal shine sweep.
const RewardCoin = ({ size = 22 }) => (
  <Box
    component="span"
    sx={{
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: size,
      height: size,
      borderRadius: '50%',
      flexShrink: 0,
      overflow: 'hidden',
      background: 'linear-gradient(145deg, #FFE9A8 0%, #F5B93F 45%, #C8860A 100%)',
      border: '1px solid #B87D0A',
      boxShadow: '0 1px 3px rgba(120,72,0,0.35), inset 0 1px 1px rgba(255,255,255,0.7), inset 0 -1px 2px rgba(120,72,0,0.35)',
    }}
  >
    <WorkspacePremiumIcon sx={{ fontSize: size * 0.64, color: '#7A4E00', position: 'relative', zIndex: 1 }} />
    <Box
      sx={{
        position: 'absolute',
        top: '-50%',
        left: '-60%',
        width: '50%',
        height: '220%',
        background: 'linear-gradient(120deg, transparent 20%, rgba(255,255,255,0.9) 50%, transparent 80%)',
        transform: 'rotate(20deg)',
        animation: 'coinShine 3.4s ease-in-out infinite',
        '@keyframes coinShine': {
          '0%': { left: '-60%' },
          '35%': { left: '140%' },
          '100%': { left: '140%' },
        },
      }}
    />
  </Box>
);

// Number + coin — replaces plain "N Coins" text wherever a value is shown
const PointsBadge = ({ amount, size = 20, fontSize, color = 'inherit', fontWeight = 700 }) => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
    <Typography component="span" sx={{ fontSize, fontWeight, color, lineHeight: 1 }}>
      {(amount || 0).toFixed(0)}
    </Typography>
    <RewardCoin size={size} />
  </Box>
);

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'earned', label: 'Earned' },
  { key: 'reversed', label: 'Reversed' },
  { key: 'expired', label: 'Expired' },
];

const STATUS_LABEL = {
  locked: 'Locked',
  active: 'Available',
  partially_reversed: 'Partially reversed',
};

const ActivityRow = ({ item }) => {
  const isCredit = item.type === 'credit';
  const isActiveCredit = isCredit && (item.txn_status === 'active' || item.txn_status === 'partially_reversed' || item.txn_status === 'locked');
  const remaining = isActiveCredit ? daysUntil(item.expiry_date) : null;
  const expiringSoon = remaining !== null && remaining <= 2 && remaining >= 0;
  const isLocked = item.txn_status === 'locked';

  return (
    <Box sx={{
      py: 1.75, borderBottom: '1px solid #F0F0F0',
      display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'
    }}>
      <Box sx={{ minWidth: 0, pr: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="body2" fontWeight="600" noWrap>
            {item.title}
          </Typography>
          {isLocked && (
            <Chip
              size="small"
              icon={<LockIcon sx={{ fontSize: 12 }} />}
              label="Locked"
              sx={{ height: 18, fontSize: 10, bgcolor: '#F1F5F9', color: '#475569' }}
            />
          )}
        </Box>
        {item.subtitle && (
          <Typography variant="caption" color="text.secondary" display="block">
            {item.subtitle}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: '#B0B0B0' }} display="block">
          {formatDate(item.date)}{item.order_id ? ` • #${item.order_id}` : ''}
        </Typography>
        {isActiveCredit && item.expiry_date && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <ScheduleIcon sx={{ fontSize: 13, color: expiringSoon ? '#B45309' : 'text.secondary' }} />
            <Typography
              variant="caption"
              sx={{ color: expiringSoon ? '#B45309' : 'text.secondary', fontWeight: expiringSoon ? 700 : 400 }}
            >
              Expires {formatDateOnly(item.expiry_date)}
              {remaining !== null && ` (${remaining === 0 ? 'today' : `${remaining}d left`})`}
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, pt: 0.25, flexShrink: 0 }}>
        <Typography
          variant="body2"
          fontWeight="700"
          sx={{ color: isCredit ? '#059669' : '#B91C1C', whiteSpace: 'nowrap' }}
        >
          {isCredit ? '+' : '-'}{(item.amount || 0).toFixed(0)}
        </Typography>
        <RewardCoin size={16} />
      </Box>
    </Box>
  );
};

// ─── Promo card for a single service, driven by the admin's reward config ──
const RewardServiceCard = ({ serviceType, config, isBest, onBook }) => {
  const meta = SERVICE_META[serviceType];
  if (!meta) return null;
  const Icon = meta.icon;
  const pct = config.max_redemption_percentage || 0;

  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: 3,
        p: 2.5,
        minWidth: 260,
        flex: '1 1 260px',
        color: '#fff',
        background: meta.gradient,
        overflow: 'hidden',
        boxShadow: isBest
          ? '0 8px 24px rgba(0,0,0,0.18), 0 0 0 2px rgba(255,255,255,0.35)'
          : '0 4px 14px rgba(0,0,0,0.10)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': { transform: 'translateY(-3px)' },
      }}
    >
      {/* Decorative background icon */}
      <Icon sx={{ position: 'absolute', right: -14, bottom: -14, fontSize: 108, opacity: 0.14 }} />

      {isBest && (
        <Chip
          icon={<BoltIcon sx={{ fontSize: 14, color: '#7A4E00 !important' }} />}
          label="Best Rewards"
          size="small"
          sx={{
            position: 'absolute', top: 12, right: 12,
            bgcolor: '#FFD54F', color: '#7A4E00', fontWeight: 800, fontSize: 11,
            animation: 'flashPulse 1.8s ease-in-out infinite',
            '@keyframes flashPulse': {
              '0%, 100%': { boxShadow: '0 0 0 0 rgba(255,213,79,0.55)' },
              '50%': { boxShadow: '0 0 0 6px rgba(255,213,79,0)' },
            },
          }}
        />
      )}

      <Box sx={{
        width: 42, height: 42, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
      }}>
        <Icon sx={{ fontSize: 22 }} />
      </Box>

      <Typography sx={{ fontWeight: 800, fontSize: 15, mb: 0.3 }}>{meta.label}</Typography>
      <Typography sx={{ fontSize: 12, opacity: 0.9, mb: 1.5, lineHeight: 1.4 }}>
        {meta.tagline}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: 1.5 }}>
        <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>
          {pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(1)}%
        </Typography>
        <Typography sx={{ fontSize: 12, opacity: 0.9 }}>redeemable on bill</Typography>
      </Box>

      <Button
        fullWidth
        onClick={() => onBook(meta.route)}
        endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
        sx={{
          bgcolor: 'rgba(255,255,255,0.95)', color: meta.accent,
          textTransform: 'none', fontWeight: 700, fontSize: 13,
          borderRadius: 2, py: 0.8,
          '&:hover': { bgcolor: '#fff' },
        }}
      >
        Book Now
      </Button>
    </Box>
  );
};

const RewardServiceCardSkeleton = () => (
  <Box sx={{ minWidth: 260, flex: '1 1 260px' }}>
    <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
  </Box>
);

const ACTIVITY_PREVIEW_COUNT = 3;

const ReferralPage = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showAllActivity, setShowAllActivity] = useState(false);
  const [dialog, setDialog] = useState({ open: false, message: '', type: 'success', title: '' });

  const [rewardConfigs, setRewardConfigs] = useState(null);
  const [rewardConfigsLoading, setRewardConfigsLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    fetchSummary();
    fetchRewardConfigs();
  }, []);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await apiRequest(`${REFERRAL_API}/referral/summary`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to load referral data');
      const data = await response.json();
      setSummary(data);
    } catch (err) {
      setDialog({ open: true, message: err.message, type: 'error', title: 'Error' });
    } finally {
      setLoading(false);
    }
  };

  // Pulls the admin-configured redemption % per service so we can promote
  // whichever service currently pays the best rewards, with real numbers.
  const fetchRewardConfigs = async () => {
    setRewardConfigsLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const response = await apiRequest(`${REFERRAL_API}/admin/reward-config`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('failed');
      const data = await response.json();
      if (data.success) setRewardConfigs(data.configs);
    } catch (err) {
      // Fail silently — promo section just won't render if configs aren't reachable
      setRewardConfigs(null);
    } finally {
      setRewardConfigsLoading(false);
    }
  };

  const handleCopyCode = () => {
    if (!summary?.referral_code) return;
    navigator.clipboard.writeText(summary.referral_code);
    setDialog({ open: true, message: 'Referral code copied!', type: 'success', title: 'Copied' });
  };

  const handleShare = async () => {
    const shareText = `Use my referral code "${summary?.referral_code}" to sign up on Ayuja and get a welcome bonus! 🎁`;
    if (navigator.share) {
      try {
        await navigator.share({ title: 'Join Ayuja', text: shareText });
      } catch (e) { /* user cancelled share, ignore */ }
    } else {
      navigator.clipboard.writeText(shareText);
      setDialog({ open: true, message: 'Referral message copied to clipboard!', type: 'success', title: 'Copied' });
    }
  };

  const handleBookService = (route) => {
    if (route) navigate(route);
  };

  const filteredActivity = useMemo(() => {
    const activity = summary?.wallet_activity || [];
    if (activeTab === 'all') return activity;
    return activity.filter((a) => a.category === activeTab);
  }, [summary, activeTab]);

  // Collapse back to the 3-item preview whenever the tab changes
  useEffect(() => {
    setShowAllActivity(false);
  }, [activeTab]);

  const visibleActivity = showAllActivity
    ? filteredActivity
    : filteredActivity.slice(0, ACTIVITY_PREVIEW_COUNT);
  const hasMoreActivity = filteredActivity.length > ACTIVITY_PREVIEW_COUNT;

  // Only services the admin has redemption enabled for, sorted best-value first
  const promotedServices = useMemo(() => {
    if (!rewardConfigs) return [];
    const entries = Object.entries(rewardConfigs)
      .filter(([, cfg]) => cfg.redemption_enabled && (cfg.max_redemption_percentage || 0) > 0);
    entries.sort((a, b) => (b[1].max_redemption_percentage || 0) - (a[1].max_redemption_percentage || 0));
    return entries;
  }, [rewardConfigs]);

  const bestServiceType = promotedServices[0]?.[0];

  const expiringSoon = summary?.expiring_soon;
  const showExpiryBanner = expiringSoon && expiringSoon.count > 0 && expiringSoon.amount > 0;
  const hasLockedPoints = (summary?.locked_points || 0) > 0;

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#006D77' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)}
            sx={{ color: "#006D77", border: "1px solid #E5E7EB", bgcolor: "#fff", "&:hover": { bgcolor: "#F3F4F6" } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" fontWeight="bold" color="#006D77" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <GiftIcon sx={{ fontSize: 40 }} />
            Refer & Earn
          </Typography>
        </Box>

        {showExpiryBanner && (
          <Alert
            severity="warning"
            icon={<ScheduleIcon />}
            sx={{
              mb: 3, borderRadius: 2, bgcolor: '#FFF7ED', color: '#92400E',
              border: '1px solid #FDE68A', '& .MuiAlert-icon': { color: '#B45309' },
            }}
          >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, verticalAlign: 'middle' }}>
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontWeight: 700 }}>
                {(expiringSoon.amount || 0).toFixed(0)} <RewardCoin size={16} />
              </Box>
              {expiringSoon.count > 1 ? ` (across ${expiringSoon.count} rewards)` : ''} will expire within the next {expiringSoon.within_days} days — use your available points before they lapse.
            </Box>
          </Alert>
        )}

        {/* Referral Code + Wallet Summary */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={7}>
            <Card sx={{ p: 3, background: 'linear-gradient(135deg, #006D77 0%, #005560 100%)', color: 'white', borderRadius: 3 }}>
              <Typography variant="subtitle2" sx={{ opacity: 0.85, mb: 1 }}>
                Your Referral Code
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Typography variant="h3" fontWeight="bold" sx={{ letterSpacing: 2 }}>
                  {summary?.referral_code || '—'}
                </Typography>
                <IconButton onClick={handleCopyCode} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)' }}>
                  <CopyIcon />
                </IconButton>
              </Box>

              <Typography
                variant="body2"
                sx={{ opacity: 0.9, mb: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}
              >
                Share this code — you earn
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontWeight: 700 }}>
                  {(summary?.referrer_bonus_points || 0).toFixed(0)} <RewardCoin size={16} />
                </Box>
                when your friend places their first order, and they get
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontWeight: 700 }}>
                  {(summary?.welcome_bonus_points || 0).toFixed(0)} <RewardCoin size={16} />
                </Box>
                as a welcome bonus.
              </Typography>

              <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', mb: 2 }}>
                Points are credited after the qualifying order, stay locked for {summary?.return_window_days ?? 7} days
                (in case of returns), and expire within {summary?.min_expiry_days ?? 1}–{summary?.max_expiry_days ?? 30} days of being credited.
              </Typography>

              <Button
                variant="contained"
                startIcon={<ShareIcon />}
                onClick={handleShare}
                sx={{ bgcolor: 'white', color: '#006D77', textTransform: 'none', fontWeight: 700, '&:hover': { bgcolor: '#F0F9FF' } }}
              >
                Share Code
              </Button>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card sx={{ p: 3, borderRadius: 3, height: '80%', border: '1px solid #E0E0E0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <WalletIcon sx={{ color: '#006D77' }} />
                <Typography variant="subtitle2" color="text.secondary">Reward Coins</Typography>
              </Box>

              <Box sx={{ mb: 0.5 }}>
                <PointsBadge
                  amount={summary?.spendable_points ?? summary?.wallet_balance}
                  size={30}
                  fontSize={34}
                  fontWeight={800}
                  color="#006D77"
                />
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Available to redeem now
              </Typography>

              {hasLockedPoints && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                  <LockIcon sx={{ fontSize: 15, color: '#94A3B8' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PointsBadge amount={summary?.locked_points} size={14} fontSize={12} color="text.secondary" fontWeight={600} />
                    <Typography variant="caption" color="text.secondary">
                      still locked (pending return window)
                    </Typography>
                  </Box>
                </Box>
              )}

              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Total Referrals</Typography>
                  <Typography variant="h6" fontWeight="bold">{summary?.total_referrals || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Earned from Referrals</Typography>
                  <Box sx={{ mt: 0.25 }}>
                    <PointsBadge amount={summary?.total_earned_from_referrals} size={18} fontSize={20} fontWeight={800} color="#059669" />
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>

        {/* Maximize Your Rewards — admin-configured per-service redemption rates */}
        {(rewardConfigsLoading || promotedServices.length > 0) && (
          <Paper sx={{ p: 3, borderRadius: 3, mb: 3, border: '1px solid #E0E0E0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <TrendingUpIcon sx={{ color: '#006D77' }} />
              <Typography variant="h6" fontWeight="bold">Maximize Your Rewards</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Redemption rates below are set by Ayuja and updated regularly — book the highest-paying service to get the most out of your points.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1 }}>
              {rewardConfigsLoading
                ? [1, 2, 3].map((i) => <RewardServiceCardSkeleton key={i} />)
                : promotedServices.map(([serviceType, cfg]) => (
                    <RewardServiceCard
                      key={serviceType}
                      serviceType={serviceType}
                      config={cfg}
                      isBest={serviceType === bestServiceType}
                      onBook={handleBookService}
                    />
                  ))
              }
            </Box>
          </Paper>
        )}

        {/* Wallet Activity — tabbed transaction ledger */}
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ActivityIcon sx={{ color: '#006D77' }} />
            <Typography variant="h6" fontWeight="bold">Wallet Activity</Typography>
          </Box>

          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            sx={{
              mb: 1,
              minHeight: 36,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 36, fontSize: 14, color: 'text.secondary' },
              '& .Mui-selected': { color: '#006D77 !important' },
              '& .MuiTabs-indicator': { bgcolor: '#006D77' },
            }}
          >
            {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
          </Tabs>

          {filteredActivity.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">
                {activeTab === 'all' ? 'No activity yet.' : `No ${activeTab} activity yet.`}
              </Typography>
            </Box>
          ) : (
            <Box>
              {visibleActivity.map((item, idx) => <ActivityRow key={idx} item={item} />)}

              {hasMoreActivity && (
                <Box sx={{ textAlign: 'center', pt: 2 }}>
                  <Button
                    onClick={() => setShowAllActivity((v) => !v)}
                    sx={{
                      textTransform: 'none', fontWeight: 700, fontSize: 13.5,
                      color: '#006D77', px: 2.5, py: 0.6, borderRadius: 5,
                      border: '1px solid #B2DFDB',
                      '&:hover': { bgcolor: '#E0F7FA', border: '1px solid #006D77' },
                    }}
                  >
                    {showAllActivity ? 'Show Less' : `See More (${filteredActivity.length - ACTIVITY_PREVIEW_COUNT} more)`}
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Container>

      <CustomDialog
        open={dialog.open}
        message={dialog.message}
        type={dialog.type}
        title={dialog.title}
        onClose={() => setDialog({ ...dialog, open: false })}
        buttonText="OK"
      />
    </Box>
  );
};

export default ReferralPage;